import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import ExportButton from "./ExportButton";
import { downloadBlob } from "../../lib/downloadBlob";
import { exportName, exportPath } from "../../lib/exportFile";
import InstitutionPanel from "./InstitutionPanel";
import GroupAssessmentPanel from "./assessment/GroupAssessmentPanel";
import GroupFeedbackPanel from "./feedback/GroupFeedbackPanel";
import ParticipantsTab from "./groupSheet/ParticipantsTab";
import ScheduleTab from "./groupSheet/ScheduleTab";
import { TEAM, pickTab, questionnairesTodo, summaryLine, tabsFor } from "../../lib/groupSheet";

/**
 * The group's sheet, from Grupe curs (on tabs since 03.11.2026): the group on
 * one line on top, then Participanți (open by default), Program, Chestionare
 * for the team, and Instituție for a group an institution pays for. Open to
 * every admin role; what each may change is decided inside each tab.
 */
export default function GroupDetailsDrawer({ groupId, onClose, currentRole, initialTab, onTabChange, onEditSchedule }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [journey, setJourney] = useState(null);
  const [wanted, setWanted] = useState(initialTab || "participanti");
  const [todo, setTodo] = useState(false);
  const team = TEAM.includes(currentRole);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/groups/${groupId}/details`);
      setData(r.data);
      // Where each one stands (29.10.2026): its own request, so a slow one never holds the list.
      api.get(`/admin/groups/${groupId}/journey`).then((j) => setJourney(j.data)).catch(() => setJourney(null));
    } catch (e) {
      toast.error("Nu am putut încărca detaliile grupei.");
    } finally {
      setLoading(false);
    }
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  const group = data?.group;
  const tabs = useMemo(() => tabsFor(currentRole, group), [currentRole, group]);
  const tab = pickTab(wanted, tabs);
  const choose = (key) => { setWanted(key); onTabChange?.(key); };

  // Whether Chestionare asks for something (decision A): read again whenever
  // the team leaves the tab, so a sending done there clears the mark.
  useEffect(() => {
    if (!team || tab === "chestionare") return undefined;
    let alive = true;
    Promise.all([
      api.get(`/admin/groups/${groupId}/initial-assessment`).then((r) => r.data).catch(() => null),
      api.get(`/admin/groups/${groupId}/final-feedback`).then((r) => r.data).catch(() => null),
    ]).then(([a, f]) => { if (alive) setTodo(questionnairesTodo(a, f)); });
    return () => { alive = false; };
  }, [groupId, team, tab]);

  const exportCsv = (format) => downloadBlob(exportPath(`/admin/groups/${groupId}/participants/export`, null, format),
    exportName(`participanti_${group?.name || groupId}`, format), setExporting);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="group-details-drawer">
      <button className="flex-1 bg-black/30" aria-label="Închide" onClick={onClose} />
      <aside className="w-full max-w-3xl bg-white shadow-2xl overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white border-b border-[#E5E7EB] px-6 pt-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest text-[#6B7280]">Grupă curs</div>
              <div className="font-display text-lg font-bold text-[#373A45] truncate">{group?.name || "—"}</div>
            </div>
            <div className="flex items-center gap-2">
              <ExportButton
                onExport={exportCsv}
                busy={exporting}
                disabled={!data}
                className="inline-flex items-center gap-1 rounded-full bg-[#373A45] text-white text-xs font-semibold px-3 py-1.5 hover:bg-black disabled:opacity-40"
                testid="group-details-export"
              />
              <button onClick={onClose} className="rounded-full p-2 hover:bg-[#EDEEF0]" data-testid="group-details-close"><X size={18} /></button>
            </div>
          </div>
          {group && (
            <p className="mt-1 text-xs text-[#6B7280]" data-testid="group-details-summary">{summaryLine(group, data?.stats)}</p>
          )}
          <nav className="-mb-px mt-3 flex gap-1 overflow-x-auto" role="tablist" aria-label="Fișa grupei">
            {tabs.map((t) => {
              const on = t.key === tab;
              const count = t.key === "participanti" && data?.stats ? ` (${data.stats.total})` : "";
              return (
                <button key={t.key} type="button" role="tab" aria-selected={on} onClick={() => choose(t.key)}
                  data-testid={`group-tab-btn-${t.key}`}
                  className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-semibold ${on ? "border-[#965EA7] text-[#7A4986]" : "border-transparent text-[#6B7280] hover:text-[#373A45]"}`}>
                  {t.label}{count}
                  {t.key === "chestionare" && todo && (
                    <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-[#d99a2b] align-middle" title="E ceva de trimis"
                      data-testid="group-tab-todo" aria-label="E ceva de trimis" />
                  )}
                </button>
              );
            })}
          </nav>
        </header>

        {loading && (
          <div className="p-12 text-center text-[#6B7280] inline-flex w-full justify-center gap-2 items-center">
            <Loader2 className="animate-spin" size={18} /> Se încarcă…
          </div>
        )}

        {!loading && data && (
          <div className="p-6">
            {tab === "participanti" && (
              <ParticipantsTab groupId={groupId} data={data} journey={journey} currentRole={currentRole} onReload={load} />
            )}
            {tab === "program" && <ScheduleTab group={group} onEditSchedule={onEditSchedule} />}
            {tab === "chestionare" && (
              <div className="space-y-4" data-testid="group-tab-chestionare">
                <GroupAssessmentPanel groupId={groupId} groupName={group?.name} />
                {/* The final feedback questionnaire (30.10.2026): every group. */}
                <GroupFeedbackPanel groupId={groupId} groupName={group?.name} />
              </div>
            )}
            {tab === "institutie" && (
              <div data-testid="group-tab-institutie">
                {/* A cohort paid by an institution: confirm its places, list them for the invoice. */}
                <InstitutionPanel groupId={groupId} onChanged={load} />
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}
